import { useCallback, useEffect, useState } from "react";
import historicalOverridesService from "../../../../services/historicalOverridesService";
import {
  PagedMeta,
  StockHistoricalPriceItem,
} from "../../../../types/historicalOverrides";

export interface PriceRange {
  from: string; // ISO
  to: string;   // ISO
}

export interface UseStockPricesResult {
  prices: StockHistoricalPriceItem[];
  meta: PagedMeta | null;
  isLoading: boolean;
  error: string | null;
  range: PriceRange;
  fetchRange: (range: PriceRange) => Promise<void>;
}

function defaultRange(): PriceRange {
  const to = new Date();
  const from = new Date(to.getTime() - 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
}

export function useStockPrices(
  conId: number,
  initialRange?: PriceRange
): UseStockPricesResult {
  const [range, setRange] = useState<PriceRange>(() => initialRange ?? defaultRange());
  const [prices, setPrices] = useState<StockHistoricalPriceItem[]>([]);
  const [meta, setMeta] = useState<PagedMeta | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const doFetch = useCallback(
    async (r: PriceRange) => {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await historicalOverridesService.getStockPrices({
          conId,
          from: r.from,
          to: r.to,
          pageNumber: 1,
        });
        const items = [...(resp.items ?? [])].sort((a, b) =>
          a.datetimeUtc < b.datetimeUtc ? 1 : a.datetimeUtc > b.datetimeUtc ? -1 : 0
        );
        setPrices(items);
        setMeta({
          currentPage: resp.currentPage,
          itemsOnPage: resp.itemsOnPage,
          totalPages: resp.totalPages,
          totalItems: resp.totalItems,
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Failed to load prices";
        setError(msg);
        setPrices([]);
        setMeta(null);
      } finally {
        setIsLoading(false);
      }
    },
    [conId]
  );

  useEffect(() => {
    doFetch(range);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conId]);

  const fetchRange = useCallback(
    async (r: PriceRange) => {
      setRange(r);
      await doFetch(r);
    },
    [doFetch]
  );

  return { prices, meta, isLoading, error, range, fetchRange };
}
